/**
 * Sidebar Navigation — Role-Based + i18n + Design System Elevation
 *
 * Renders navigation items from ROLE_NAVIGATION[currentRole].
 * Lucide icons are resolved dynamically by name.
 * Collapses to icon-only view on toggle.
 * Labels resolved via i18n using nav key mapping from path.
 *
 * Design: Deep navy background, section grouping, active left-border accent,
 * smooth transitions, collapsed tooltip support.
 */

import { useLocation, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Landmark,
  Briefcase,
  Building2,
  Leaf,
  ShieldCheck,
  ShieldAlert,
  Shield,
  FileText,
  FileCheck,
  Settings,
  Users,
  BarChart,
  BarChart2,
  BarChart3,
  GitBranch,
  GitPullRequest,
  Calendar,
  ClipboardCheck,
  CheckSquare,
  Mail,
  PieChart,
  TrendingUp,
  Search,
  Target,
  Globe,
  Eye,
  Camera,
  BookOpen,
  Sparkles,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ROLE_NAVIGATION, type NavItem } from '@/config/roles';
import { useTranslation } from '@/i18n';

// ─── i18n key mapping from path to nav translation key ──────

const PATH_TO_I18N_KEY: Record<string, string> = {
  '/': 'nav.dashboard',
  '/funds': 'nav.funds',
  '/investments': 'nav.investments',
  '/investees': 'nav.investees',
  '/esg': 'nav.esg',
  '/compliance': 'nav.compliance',
  '/reports': 'nav.reports',
  '/settings': 'nav.settings',
  '/users': 'nav.users',
  '/sfdr': 'nav.sfdr',
  '/pai': 'nav.pai',
  '/taxonomy': 'nav.taxonomy',
  '/filings': 'nav.filings',
  '/calendar': 'nav.calendar',
  '/submit': 'nav.submit',
  '/submissions': 'nav.submissions',
  '/benchmarks': 'nav.benchmarks',
  '/messages': 'nav.messages',
  '/portfolio': 'nav.portfolio',
  '/performance': 'nav.performance',
  '/esg-summary': 'nav.esgSummary',
  '/pipeline': 'nav.pipeline',
  '/due-diligence': 'nav.dueDiligence',
  '/impact': 'nav.impact',
  '/sdg': 'nav.sdg',
  '/audit': 'nav.audit',
  '/snapshots': 'nav.snapshots',
  '/knowledge-graph': 'nav.knowledgeGraph',
  '/geographic': 'nav.geographic',
  '/bi': 'nav.bi',
  '/ai': 'nav.ai',
  '/tasks': 'nav.tasks',
  '/csrd': 'nav.csrd',
  '/regulatory': 'nav.regulatory',
};

// ─── Icon Resolution ───────────────────────────────────────────

const ICON_MAP: Record<string, LucideIcon> = {
  'layout-dashboard': LayoutDashboard,
  'landmark': Landmark,
  'briefcase': Briefcase,
  'building-2': Building2,
  'leaf': Leaf,
  'shield-check': ShieldCheck,
  'shield-alert': ShieldAlert,
  'shield': Shield,
  'file-text': FileText,
  'file-check': FileCheck,
  'settings': Settings,
  'users': Users,
  'bar-chart': BarChart,
  'bar-chart-2': BarChart2,
  'bar-chart-3': BarChart3,
  'git-branch': GitBranch,
  'git-pull-request': GitPullRequest,
  'calendar': Calendar,
  'clipboard-check': ClipboardCheck,
  'check-square': CheckSquare,
  'mail': Mail,
  'pie-chart': PieChart,
  'trending-up': TrendingUp,
  'search': Search,
  'target': Target,
  'globe': Globe,
  'eye': Eye,
  'camera': Camera,
  'book-open': BookOpen,
  'sparkles': Sparkles,
  'network': Network,
};

// ─── Section Grouping ──────────────────────────────────────────

interface NavSection {
  label: string;
  paths: string[];
}

const SECTION_DEFS: NavSection[] = [
  { label: 'PORTFOLIO', paths: ['/', '/funds', '/investments', '/investees', '/portfolio', '/performance', '/pipeline', '/due-diligence'] },
  { label: 'DATA COLLECTION', paths: ['/esg', '/submit', '/submissions', '/compliance', '/reports', '/csrd', '/sfdr', '/pai', '/taxonomy', '/filings', '/calendar', '/regulatory'] },
  { label: 'INTELLIGENCE', paths: ['/bi', '/ai', '/knowledge-graph', '/geographic', '/benchmarks', '/impact', '/sdg', '/esg-summary', '/audit', '/snapshots'] },
  { label: 'ECOSYSTEM', paths: ['/tasks', '/messages'] },
  { label: 'ADMIN', paths: ['/users', '/settings'] },
];

function groupNavItems(items: NavItem[]): { label: string; items: NavItem[] }[] {
  const result: { label: string; items: NavItem[] }[] = [];
  const used = new Set<string>();

  for (const section of SECTION_DEFS) {
    const sectionItems = items.filter((item) => section.paths.includes(item.path));
    if (sectionItems.length > 0) {
      result.push({ label: section.label, items: sectionItems });
      for (const item of sectionItems) used.add(item.path);
    }
  }

  // Catch any ungrouped items
  const remaining = items.filter((item) => !used.has(item.path));
  if (remaining.length > 0) {
    result.push({ label: 'OTHER', items: remaining });
  }

  return result;
}

// ─── Component ─────────────────────────────────────────────────

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Called when a nav link is clicked (used to close mobile overlay) */
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onToggle, onNavigate }: SidebarProps) {
  const { pathname } = useLocation();
  const { icRole } = useAuth();
  const { t } = useTranslation();

  const navItems = ROLE_NAVIGATION[icRole] ?? [];
  const sections = groupNavItems(navItems);

  return (
    <aside
      data-tour="sidebar"
      className={clsx(
        'flex h-full flex-col bg-[#0F1729] transition-all duration-200',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      {/* Branding */}
      <div className="flex h-12 items-center gap-3 border-b border-white/[0.06] px-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-blue-600 text-[10px] font-bold text-white shadow-sm">
          IC
        </div>
        {!collapsed && (
          <div className="flex flex-col truncate">
            <span className="text-[13px] font-semibold tracking-tight text-white">
              ImpactCore
            </span>
            <span className="text-[9px] text-slate-500 leading-tight">
              by Society Intelligence
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {sections.map((section, sectionIdx) => (
          <div key={section.label} className={clsx(sectionIdx > 0 && 'mt-3')}>
            {/* Section Label */}
            {!collapsed && (
              <div className="mb-1 px-3">
                <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-600">
                  {section.label}
                </span>
              </div>
            )}
            {collapsed && sectionIdx > 0 && (
              <div className="mx-2.5 mb-1.5 border-t border-white/[0.06]" />
            )}

            <ul className="flex flex-col gap-px px-2">
              {section.items.map((item) => {
                const Icon = ICON_MAP[item.icon] ?? LayoutDashboard;
                const isActive =
                  item.path === '/'
                    ? pathname === '/'
                    : pathname.startsWith(item.path);

                // Resolve i18n label, falling back to the hardcoded label from roles config
                const i18nKey = PATH_TO_I18N_KEY[item.path];
                const label = i18nKey ? t(i18nKey) : item.label;

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      title={collapsed ? label : undefined}
                      onClick={onNavigate}
                      className={clsx(
                        'relative flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[12px] font-medium transition-all duration-150',
                        isActive
                          ? 'bg-white/[0.08] text-white ic-nav-active'
                          : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-200',
                        collapsed && 'justify-center px-0',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                      {!collapsed && <span className="truncate">{label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse Toggle */}
      <button
        onClick={onToggle}
        className="flex h-10 items-center justify-center border-t border-white/[0.06] text-slate-500 hover:text-slate-300 transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <PanelLeftOpen className="h-4 w-4" />
        ) : (
          <PanelLeftClose className="h-4 w-4" />
        )}
      </button>
    </aside>
  );
}
